import React, { useMemo } from "react";
import { Grid } from "@mui/material";
import classnames from "classnames";
import { format, isSameDay, isSameMonth, parseISO } from "date-fns";
import equal from "fast-deep-equal/es6";

import { isLosAngelesPST } from "@formant/date-fns/isLosAngelesPST";
import { IOrganization } from "@formant/core/model/IOrganization";
import { IUsageCost } from "@formant/core/model/IUsageCost";

import { useStores } from "../../../context/StoreContext";
import { formatCentsToDollarString } from "../../../utils/formatCentsToDollarString";
import { getTotalUsage } from "../../../utils/usageCost";
import { CircleButton } from "../../common/CircleButton";
import { Heading3 } from "../../common/Heading3";
import { Help } from "../../common/Help";
import { Section } from "../../common/Section";
import { Total } from "../DataUsage/Total";
import { UsageItem } from "../DataUsage/UsageItem";
import { BillingAlert } from "./BillingAlert";
import { DoughnutChart } from "./DoughnutChart";
import styles from "./index.scss";
import { UsageButtonGroup } from "./UsageButtonGroup";
import { UsageTable } from "./UsageTable";

export interface IBillingUsageProps {
    start: Date;
    end: Date;
    onNext: () => void;
    onPrevious: () => void;
    organization: IOrganization;
    usageCost: IUsageCost[];
}

export const BillingUsage = React.memo(
    ({
        start,
        end,
        onNext,
        onPrevious,
        organization,
        usageCost,
    }: IBillingUsageProps) => {
        const { deviceStore } = useStores();

        const devicesPerBudget =
            deviceStore.list()?.filter(device => device.enabled)?.length || 0;

        const isCurrentDate = useMemo(
            () => isSameMonth(start, new Date()),
            [start],
        );

        const usageCostData = useMemo(() => {
            return usageCost.find(_ => {
                // remove date offset from string to avoid incorrect format of date
                const parsedTermStart = _.termStart
                    ? parseISO(_.termStart.slice(0, 10))
                    : false;

                // remove date offset from string to avoid incorrect format of date
                const parsedTermEnd = _.termEnd
                    ? parseISO(_.termEnd.slice(0, 10))
                    : false;

                return (
                    parsedTermStart &&
                    isSameMonth(parsedTermStart, start) &&
                    parsedTermEnd &&
                    isSameMonth(parsedTermEnd, end)
                );
            });
        }, [usageCost, start, end]);

        const totalUsage = useMemo(
            () =>
                getTotalUsage(
                    usageCostData && usageCostData?.costItems.length > 0
                        ? usageCostData?.costItems
                        : [],
                ),
            [usageCostData, getTotalUsage],
        );

        const isDownloadEnabled = !!usageCostData && totalUsage > 0;

        const alertAmount = useMemo(
            () =>
                (organization?.billingInfo.organizationBudgetUsdCents || 0) +
                (organization?.billingInfo.deviceBudgetUsdCents || 0),
            [
                organization?.billingInfo.organizationBudgetUsdCents,
                organization?.billingInfo.deviceBudgetUsdCents,
            ],
        );

        const alertAmountTotal =
            organization.billingInfo.deviceBudgetUsdCents != null
                ? alertAmount * devicesPerBudget
                : alertAmount;

        const isOverBudget = alertAmountTotal - totalUsage < 0;

        const isAlertEnabled = useMemo(
            () =>
                Boolean(
                    organization?.billingInfo.organizationBudgetUsdCents ||
                    organization?.billingInfo.deviceBudgetUsdCents,
                ),

            [
                organization?.billingInfo.organizationBudgetUsdCents,
                organization?.billingInfo.deviceBudgetUsdCents,
            ],
        );

        const timezone = isLosAngelesPST(start) ? "UTC-8" : "UTC-7";
        const timezoneMessage = `Billing cycles begin on the first of the month in the ${timezone} time zone`;
        return (
            <Section
                title="Usage"
                className={styles["data-usage"]}
                actions={
                    <>
                        <Heading3
                            className={classnames(
                                styles["dark-text"],
                                styles["center-text"],
                            )}
                        >
                            {`${format(start, "MMMM yyyy")} ${timezone}`}{" "}
                            <Help
                                className={styles["timezone-help-icon"]}
                                text={timezoneMessage}
                            />
                        </Heading3>

                        <CircleButton icon="previous" onClick={onPrevious} />
                        <CircleButton
                            disabled={isCurrentDate}
                            icon="next"
                            onClick={onNext}
                        />
                    </>
                }
            >
                <Grid container={true} columnSpacing={2} rowSpacing={2}>
                    <Grid
                        justifyContent="center"
                        alignItems="center"
                        alignSelf="center"
                        item={true}
                        xs={12}
                    >
                        <UsageItem
                            className={styles["usage-item-container"]}
                            large={true}
                        >
                            <Heading3>usage spend</Heading3>
                            <Total>
                                {usageCostData && totalUsage > 0 ? (
                                    <>
                                        {`${formatCentsToDollarString(
                                            totalUsage,
                                        )} ${isCurrentDate ? "estimated" : ""}`}
                                    </>
                                ) : (
                                    <p
                                        className={classnames(
                                            styles["dark-text"],
                                            styles["no-usage-total"],
                                        )}
                                    >
                                        No usage data
                                    </p>
                                )}
                            </Total>
                        </UsageItem>
                    </Grid>
                </Grid>
                <Grid container={true} columnSpacing={4} rowSpacing={4}>
                    <>
                        <Grid
                            justifyContent="center"
                            alignItems="center"
                            alignSelf="center"
                            item={true}
                            xs={6}
                        >
                            <DoughnutChart
                                isCurrentDate={isCurrentDate}
                                isAlertEnabled={isAlertEnabled}
                                isOverBudget={isOverBudget}
                                alertAmount={alertAmountTotal}
                                organization={organization}
                                totalUsage={totalUsage}
                                usageData={usageCostData}
                            />
                        </Grid>
                        <Grid
                            justifyContent="center"
                            alignItems="center"
                            alignSelf="center"
                            item={true}
                            xs={6}
                        >
                            <UsageTable usageData={usageCostData} />
                            <br />
                            <UsageButtonGroup
                                isUsageDataAvailable={isDownloadEnabled}
                                organizationId={organization.id}
                                start={start}
                                end={end}
                            />
                        </Grid>
                    </>
                </Grid>

                {isOverBudget && isAlertEnabled && isCurrentDate ? (
                    <BillingAlert
                        prepaidAmount={usageCostData?.prepaidAmount}
                    />
                ) : null}
            </Section>
        );
    },
    (prevProps, nextProps) => {
        const { start: prevStart, end: prevEnd, ...prev } = prevProps;
        const { start: nextStart, end: nextEnd, ...next } = nextProps;
        return (
            isSameDay(prevStart, nextStart) &&
            isSameDay(prevEnd, nextEnd) &&
            equal(prev, next)
        );
    },
);
